// composables/useGlobalMemoryManagement.js
import { onUnmounted, getCurrentInstance } from 'vue';

// Global registry to track all components
const componentRegistry = new Map();

export function useGlobalMemoryManagement() {
  const instance = getCurrentInstance();
  const cleanupCallbacks = [];
  const intervals = [];
  const timeouts = [];
  const eventListeners = [];

  if (!instance) return {};

  const componentId = instance.uid;
  const componentName = instance.type.name || 'AnonymousComponent';

  // Register component
  componentRegistry.set(componentId, {
    name: componentName,
    cleanupCallbacks,
    intervals,
    timeouts,
    eventListeners
  });

  const addCleanup = (callback) => {
    cleanupCallbacks.push(callback);
  };

  const addInterval = (intervalId) => {
    intervals.push(intervalId);
  };

  const addTimeout = (timeoutId) => {
    timeouts.push(timeoutId);
  };

  const addEventListener = (element, event, handler, options) => {
    element.addEventListener(event, handler, options);
    eventListeners.push({ element, event, handler });
  };

  const forceCleanup = () => {
    // Clear intervals
    intervals.forEach(interval => clearInterval(interval));
    intervals.length = 0;
    
    // Clear timeouts
    timeouts.forEach(timeout => clearTimeout(timeout));
    timeouts.length = 0;
    
    // Remove event listeners
    eventListeners.forEach(({ element, event, handler }) => {
      element.removeEventListener(event, handler);
    });
    eventListeners.length = 0;
    
    // Execute cleanup callbacks
    cleanupCallbacks.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.warn(`Cleanup error in ${componentName}:`, error);
      }
    });
    cleanupCallbacks.length = 0;
    
    // Remove from registry
    componentRegistry.delete(componentId);
    
    console.log(`🧹 Cleaned up ${componentName}`);
  };

  // Auto cleanup on unmount
  onUnmounted(() => {
    forceCleanup();
  });

  return {
    addCleanup,
    addInterval,
    addTimeout,
    addEventListener,
    forceCleanup
  };
}

// Global cleanup function
export function forceGlobalCleanup() {
  // console.log(`🧹 Force cleaning up ${componentRegistry.size} components`);
  componentRegistry.forEach((component, id) => {
    try {
      // Clear all registered items
      component.intervals?.forEach(interval => clearInterval(interval));
      component.timeouts?.forEach(timeout => clearTimeout(timeout));
      component.eventListeners?.forEach(({ element, event, handler }) => {
        element.removeEventListener(event, handler);
      });
      component.cleanupCallbacks?.forEach(callback => callback());
    } catch (error) {
      console.warn(`Error cleaning up ${component.name}:`, error);
    }
  });
  componentRegistry.clear();
}

// Get component count for monitoring
export function getActiveComponentCount() {
  return componentRegistry.size;
}